import { http, orUndefined } from "./http";
import { knownCycleLabel } from "./schoolService";
import type { SchoolClass, Subject } from "@/types";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

interface ApiClass {
  id: number;
  cycleId: number;
  name: string;
  shortName: string | null;
  capacity: number | null;
  level: number | null;
  subjectIds: number[];
}

interface ApiSubject {
  id: number;
  cycleId: number;
  name: string;
  coefficient: number | null;
}

const toClass = (c: ApiClass): SchoolClass => ({
  id: String(c.id),
  cycleId: String(c.cycleId),
  name: c.name,
  shortName: c.shortName ?? undefined,
  capacity: c.capacity ?? undefined,
  level: c.level ?? undefined,
  subjectIds: (c.subjectIds ?? []).map(String),
});

const toSubject = (s: ApiSubject): Subject => ({
  id: String(s.id),
  cycleId: String(s.cycleId),
  name: s.name,
  coefficient: s.coefficient ?? undefined,
});

/** Champs modifiables d'une classe (le cycle est immuable côté backend). */
const classBody = (c: Partial<SchoolClass>) => ({
  name: c.name?.trim(),
  shortName: orUndefined(c.shortName),
  capacity: c.capacity || undefined,
  level: c.level || undefined,
});

/** Aligne les matières d'une classe sur la liste voulue (ajouts puis retraits). */
async function syncClassSubjects(classId: string, current: string[], wanted: string[]): Promise<void> {
  const toAdd = wanted.filter((id) => !current.includes(id));
  const toRemove = current.filter((id) => !wanted.includes(id));
  if (toAdd.length) await http.post(`/classes/${classId}/subjects`, { subjectIds: toAdd.map(Number) });
  for (const subjectId of toRemove) await http.delete(`/classes/${classId}/subjects/${subjectId}`);
}

/* -------------------------------------------------------------------------- */
/* Classes                                                                     */
/* -------------------------------------------------------------------------- */

export async function getClasses(): Promise<SchoolClass[]> {
  return (await http.getAll<ApiClass>("/classes")).map(toClass);
}

export async function addClass(payload: Omit<SchoolClass, "id">): Promise<SchoolClass> {
  const created = toClass(
    await http.post<ApiClass>("/classes", { ...classBody(payload), cycleId: Number(payload.cycleId) }),
  );
  await syncClassSubjects(created.id, [], payload.subjectIds);
  return { ...created, subjectIds: payload.subjectIds };
}

export async function updateClass(id: string, patch: Partial<SchoolClass>): Promise<SchoolClass> {
  const current = toClass(await http.get<ApiClass>(`/classes/${id}`));
  const updated = toClass(await http.put<ApiClass>(`/classes/${id}`, classBody({ ...current, ...patch })));
  if (patch.subjectIds) await syncClassSubjects(id, current.subjectIds, patch.subjectIds);
  return { ...updated, subjectIds: patch.subjectIds ?? current.subjectIds };
}

export async function deleteClass(id: string): Promise<void> {
  await http.delete(`/classes/${id}`);
}

/* -------------------------------------------------------------------------- */
/* Matières                                                                    */
/* -------------------------------------------------------------------------- */

export async function getSubjects(): Promise<Subject[]> {
  return (await http.getAll<ApiSubject>("/subjects")).map(toSubject);
}

export async function addSubject(payload: Omit<Subject, "id">): Promise<Subject> {
  return toSubject(
    await http.post<ApiSubject>("/subjects", {
      name: payload.name.trim(),
      cycleId: Number(payload.cycleId),
      coefficient: payload.coefficient || undefined,
    }),
  );
}

export async function updateSubject(id: string, patch: Partial<Subject>): Promise<Subject> {
  // Le cycle d'une matière est immuable côté backend : seuls le nom et le coefficient changent.
  return toSubject(
    await http.put<ApiSubject>(`/subjects/${id}`, {
      name: patch.name?.trim(),
      coefficient: patch.coefficient || undefined,
    }),
  );
}

export async function deleteSubject(id: string): Promise<void> {
  // Refusé par le backend tant que la matière est attribuée à une classe (message affiché tel quel).
  await http.delete(`/subjects/${id}`);
}

/** Libellé d'un cycle (cycles lus du backend). */
export function cycleLabel(cycleId: string): string {
  return knownCycleLabel(cycleId) ?? "—";
}
